import { useState } from "react";
import swal from "sweetalert";
import "./AgentRegister.css";
import {
  buildFormDataFromState,
  registerAgent,
} from "./agentRegisterService";
import {
  getDefaultAgencyPhone,
  getDefaultRegistrationLanguage,
} from "../../../config/agentRegisterConfig";

const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9]+$/;
const EMAIL_REGEX =
  /^([0-9a-zA-Z]([-_.\\]*[0-9a-zA-Z]+)*)@([0-9a-zA-Z]([-_.\\]*[0-9a-zA-Z]+)*)[.]([a-zA-Z]{2,9})$/;
const PHONE_REGEX = /^[0-9]{10}$/;

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "fr", label: "French" },
];

const GENDER_OPTIONS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
];

const toUpper = (value) => value.toUpperCase();

export default function AgentRegister() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [agencyPhone] = useState(getDefaultAgencyPhone);
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [agencyPin, setAgencyPin] = useState("");
  const [agentPassword, setAgentPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [gender, setGender] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [phone, setPhone] = useState("");
  const [language, setLanguage] = useState(getDefaultRegistrationLanguage);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [userNameError, setUserNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [agencyPinError, setAgencyPinError] = useState("");
  const [agentPasswordError, setAgentPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [genderError, setGenderError] = useState("");
  const [birthDateError, setBirthDateError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [languageError, setLanguageError] = useState("");
  const [termsError, setTermsError] = useState("");

  const handleUppercaseChange = (setter, errorSetter) => (e) => {
    errorSetter("");
    setter(toUpper(e.target.value));
  };

  const handlePhoneChange = (setter, errorSetter) => (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    errorSetter("");
    setter(digits);
  };

  const validate = () => {
    let valid = true;

    setFirstNameError("");
    setLastNameError("");
    setUserNameError("");
    setEmailError("");
    setAgencyPinError("");
    setAgentPasswordError("");
    setConfirmPasswordError("");
    setGenderError("");
    setBirthDateError("");
    setPhoneError("");
    setLanguageError("");
    setTermsError("");

    if (!firstName.trim()) {
      setFirstNameError("The first name field is required.");
      valid = false;
    } else if (!ALPHANUMERIC_REGEX.test(firstName)) {
      setFirstNameError("The firstname may only contain letters and numbers.");
      valid = false;
    }

    if (!lastName.trim()) {
      setLastNameError("The last name field is required.");
      valid = false;
    } else if (!ALPHANUMERIC_REGEX.test(lastName)) {
      setLastNameError("The lastname may only contain letters and numbers.");
      valid = false;
    }

    if (!agencyPhone.trim() || !PHONE_REGEX.test(agencyPhone)) {
      valid = false;
    }

    if (!userName.trim()) {
      setUserNameError("The username field is required.");
      valid = false;
    } else if (userName.length > 10) {
      setUserNameError("The username may not be greater than 10 characters.");
      valid = false;
    } else if (!ALPHANUMERIC_REGEX.test(userName)) {
      setUserNameError("The username may only contain letters and numbers.");
      valid = false;
    }

    if (!email.trim()) {
      setEmailError("The agency email field is required.");
      valid = false;
    } else if (!EMAIL_REGEX.test(email)) {
      setEmailError("Please enter a valid email address.");
      valid = false;
    }

    if (!agencyPin.trim()) {
      setAgencyPinError("The agency password field is required.");
      valid = false;
    }

    if (!agentPassword.trim()) {
      setAgentPasswordError("The agent password field is required.");
      valid = false;
    }

    if (!confirmPassword) {
      setConfirmPasswordError("The confirm password field is required.");
      valid = false;
    } else if (confirmPassword !== agentPassword) {
      setConfirmPasswordError("The confirm password does not match.");
      valid = false;
    }

    if (!gender) {
      setGenderError("The gender field is required.");
      valid = false;
    }

    if (!birthDate) {
      setBirthDateError("The birth date field is required.");
      valid = false;
    }

    if (phone && !PHONE_REGEX.test(phone)) {
      setPhoneError("The mobile phone must be 10 digits.");
      valid = false;
    }

    if (!language) {
      setLanguageError("The language field is required.");
      valid = false;
    }

    if (!acceptTerms) {
      setTermsError("You must accept the terms and conditions.");
      valid = false;
    }

    return valid;
  };

  const onSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const formData = buildFormDataFromState({
        agencyPhone,
        agencyPin,
        userName,
        firstName,
        lastName,
        email,
        phone,
        gender,
        birthDate,
        language,
        agentPassword,
      });

      await registerAgent(formData);

      swal({
        text: "Registration completed successfully.",
        icon: "success",
      });
    } catch (error) {
      swal({
        text: error?.message || "Failed to register. Please try again.",
        icon: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="agent-register">
      <div className="agent-register__container">
        <div className="agent-register__main">
          <div className="agent-register__header">
            <div>
              <h1>Welcome to the Registration Form</h1>
              <p className="agent-register__subtitle">Please Register your account.</p>
            </div>
            <span className="agent-register__required-note">
              <span className="text-danger">*</span>denotes required fields
            </span>
          </div>

          <div className="row">
            <div className="col-md-6 agent-register__field">
              <label>
                First Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control agent-register__input agent-register__input--uppercase"
                value={firstName}
                onChange={handleUppercaseChange(setFirstName, setFirstNameError)}
                maxLength={50}
              />
              {firstNameError && (
                <span className="agent-register__error">{firstNameError}</span>
              )}
            </div>
            <div className="col-md-6 agent-register__field">
              <label>
                Last Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control agent-register__input agent-register__input--uppercase"
                value={lastName}
                onChange={handleUppercaseChange(setLastName, setLastNameError)}
                maxLength={50}
              />
              {lastNameError && (
                <span className="agent-register__error">{lastNameError}</span>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 agent-register__field">
              <label>
                Agency Email <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control agent-register__input agent-register__input--uppercase"
                value={email}
                onChange={handleUppercaseChange(setEmail, setEmailError)}
              />
              {emailError && (
                <span className="agent-register__error">{emailError}</span>
              )}
            </div>
            <div className="col-md-6 agent-register__field">
              <label>Mobile Phone</label>
              <input
                type="text"
                className="form-control agent-register__input"
                value={phone}
                onChange={handlePhoneChange(setPhone, setPhoneError)}
                maxLength={10}
              />
              {phoneError && (
                <span className="agent-register__error">{phoneError}</span>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 agent-register__field">
              <label>
                Username <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control agent-register__input agent-register__input--uppercase"
                value={userName}
                onChange={handleUppercaseChange(setUserName, setUserNameError)}
                maxLength={10}
              />
              <span className="agent-register__helper">Used as your login ID.</span>
              {userNameError && (
                <span className="agent-register__error">{userNameError}</span>
              )}
            </div>
            <div className="col-md-6 agent-register__field">
              <label>
                Agency Password <span className="text-danger">*</span>
              </label>
              <input
                type="password"
                className="form-control agent-register__input agent-register__input--uppercase"
                value={agencyPin}
                onChange={handleUppercaseChange(setAgencyPin, setAgencyPinError)}
              />
              <span className="agent-register__helper">
                Agency manager password. Required to verify your agency.
              </span>
              {agencyPinError && (
                <span className="agent-register__error">{agencyPinError}</span>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 agent-register__field">
              <label>
                Agent Password <span className="text-danger">*</span>
              </label>
              <input
                type="password"
                className="form-control agent-register__input agent-register__input--uppercase"
                value={agentPassword}
                onChange={handleUppercaseChange(
                  setAgentPassword,
                  setAgentPasswordError
                )}
              />
              {agentPasswordError && (
                <span className="agent-register__error">{agentPasswordError}</span>
              )}
            </div>
            <div className="col-md-6 agent-register__field">
              <label>
                Confirm Password <span className="text-danger">*</span>
              </label>
              <input
                type="password"
                className="form-control agent-register__input agent-register__input--uppercase"
                value={confirmPassword}
                onChange={handleUppercaseChange(
                  setConfirmPassword,
                  setConfirmPasswordError
                )}
              />
              {confirmPasswordError && (
                <span className="agent-register__error">
                  {confirmPasswordError}
                </span>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 agent-register__field">
              <label>
                Gender <span className="text-danger">*</span>
              </label>
              <select
                className="form-control agent-register__input agent-register__select"
                value={gender}
                onChange={(e) => {
                  setGenderError("");
                  setGender(e.target.value);
                }}
              >
                <option value="">Select gender</option>
                {GENDER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {genderError && (
                <span className="agent-register__error">{genderError}</span>
              )}
            </div>
            <div className="col-md-6 agent-register__field">
              <label>
                Birth Date <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control agent-register__input"
                value={birthDate}
                onChange={(e) => {
                  setBirthDateError("");
                  setBirthDate(e.target.value);
                }}
              />
              {birthDateError && (
                <span className="agent-register__error">{birthDateError}</span>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 agent-register__field">
              <label>
                Language <span className="text-danger">*</span>
              </label>
              <select
                className="form-control agent-register__input agent-register__select"
                value={language}
                onChange={(e) => {
                  setLanguageError("");
                  setLanguage(e.target.value);
                }}
              >
                {LANGUAGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {languageError && (
                <span className="agent-register__error">{languageError}</span>
              )}
            </div>
          </div>

          <div className="agent-register__field agent-register__terms">
            <label className="agent-register__checkbox-label">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => {
                  setTermsError("");
                  setAcceptTerms(e.target.checked);
                }}
              />
              <span>
                I agree to the Terms &amp; Conditions{" "}
                <span className="text-danger">*</span>
              </span>
            </label>
            {termsError && (
              <span className="agent-register__error">{termsError}</span>
            )}
          </div>

          <button
            type="button"
            className="agent-register__submit"
            onClick={onSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Registering..." : "Register"}
          </button>
        </div>
      </div>
    </div>
  );
}
